import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import {
  Rarity,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { AnyPlayerStatsIcon } from '@sorare/marketplace/src/components/player/AnyPlayerStatsIcon';
import {
  Cell,
  lastCellPadding,
} from '@sorare/marketplace/src/components/searchPlayerTable/SearchTable/ui';
import { StartingPriceCell } from '@sorare/marketplace/src/components/searchPlayerTable/StartingPriceCell';
import { PlayerRotowireStatsProjection } from '@sorare/marketplace/src/shared/player/PlayerDetails/PlayerStats/PlayerRotowireStatsProjection';
import { InlineGame } from '@sorare/marketplace/src/shared/stats/InlineGame';

import type { FixtureTopPlayersTableRow_fixturePlayer } from '#src/components/fixture/FixtureTopPlayersTable/FixtureTopPlayersTableRow/__generated__/index.graphql.ts';
import type {
  PlayerScoreStats,
  PlayerScoreView,
} from '#src/components/fixture/FixtureTopPlayersTable/types.ts';
import {
  AnySportPlayerScoreStats,
  BaseballPlayerScoreStats,
  PlayerScoreViewAttributes,
} from '#src/components/fixture/FixtureTopPlayersTable/types.ts';

import type { BaseballScoreCells_fixturePlayer } from './__generated__/index.graphql.ts';

const ScoreCells = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  padding: var(--unit) 0;
  row-gap: var(--unit);
`;
const LastCell = styled(Cell)`
  ${lastCellPadding}
  justify-content: flex-end;
`;

const scoreShapeByMode: {
  [key in PlayerScoreStats]?: 'PILL' | 'TEXT' | 'HEXAGON';
} = {
  [AnySportPlayerScoreStats.SCORE]: 'PILL',
  [BaseballPlayerScoreStats.AB]: 'TEXT',
  [BaseballPlayerScoreStats.BB]: 'TEXT',
  [BaseballPlayerScoreStats.H]: 'TEXT',
  [BaseballPlayerScoreStats.HR]: 'TEXT',
  [BaseballPlayerScoreStats.K]: 'TEXT',
  [BaseballPlayerScoreStats.R]: 'TEXT',
  [BaseballPlayerScoreStats.RBI]: 'TEXT',
  [BaseballPlayerScoreStats.AVG]: 'TEXT',
  [BaseballPlayerScoreStats.OBP]: 'TEXT',
  [BaseballPlayerScoreStats.SLG]: 'TEXT',
};

const getRelevantMlbSo5Score = <T extends { projectedScore: number }>(
  so5Scores: T[]
): T | undefined => {
  if (so5Scores.every(({ projectedScore }) => !!projectedScore)) {
    return so5Scores.reduce<T | undefined>(
      (maxSo5Score, current) =>
        current.projectedScore > (maxSo5Score?.projectedScore || 0)
          ? current
          : maxSo5Score,
      so5Scores.at(0)
    );
  }

  return so5Scores.find(({ projectedScore }) => !!projectedScore);
};

type BaseballScoreCellsProps = {
  totalScore: number | null;
  eligibleSo5Scores: FixtureTopPlayersTableRow_fixturePlayer['eligibleSo5Scores'];
  statsManager: PlayerScoreView;
  isUpcomingFixture?: boolean;
  player: FixtureTopPlayersTableRow_fixturePlayer['player'];
  onListingsClick: () => void;
};

export const BaseballScoreCells = ({
  totalScore,
  eligibleSo5Scores,
  statsManager,
  isUpcomingFixture,
  player,
  onListingsClick,
}: BaseballScoreCellsProps) => {
  const eligibleMlbScores = eligibleSo5Scores.filter(
    isTypeFn('BaseballPlayerGameScore')
  );
  const eligibleMlbScoresToDisplay = (
    isUpcomingFixture
      ? [getRelevantMlbSo5Score(eligibleMlbScores)]
      : eligibleMlbScores
  ).filter(Boolean);

  const displayTotalScore =
    !isUpcomingFixture && eligibleMlbScoresToDisplay.length > 1;
  const gridColumnStartIndex = displayTotalScore ? 3 : 2;

  return (
    <>
      {!!displayTotalScore && typeof totalScore === 'number' && (
        <Vertical
          center
          style={{
            gridColumn: `2 / 3`,
          }}
        >
          <Score
            hideReviewing
            shape="PILL"
            score={totalScore}
            scoreStatus="FINAL"
            scoreType="PLAYER_SCORE"
            animated
            size={4}
          />
        </Vertical>
      )}
      <ScoreCells
        style={{
          gridColumn: `${gridColumnStartIndex} / ${statsManager[PlayerScoreViewAttributes.PLAYER_SCORE].length + gridColumnStartIndex}`,
        }}
      >
        {eligibleMlbScoresToDisplay.map(so5Score => {
          const { score, scoreStatus, baseballPlayerGameStats } =
            so5Score || {};
          const {
            hittingSingles,
            hittingDoubles,
            hittingTriples,
            hittingHomeRuns,
            hittingRuns,
            hittingRunsBattedIn,
            hittingAtBase,
            hittingWalks,
            hittingStrikeouts,
            hittingHitByPitch,
          } = baseballPlayerGameStats || {};

          const hits =
            (hittingSingles || 0) +
            (hittingDoubles || 0) +
            (hittingTriples || 0) +
            (hittingHomeRuns || 0);

          const playerScoreStats: {
            [key in PlayerScoreStats]?: number | ReactNode;
          } = isUpcomingFixture
            ? {
                [AnySportPlayerScoreStats.SCORE]: score,
                [BaseballPlayerScoreStats.MATCHUPS]: (
                  <Vertical>
                    <InlineGame
                      key={so5Score.anyGame.id}
                      game={so5Score.anyGame}
                      withTeamCode
                      withGameStatus
                    />
                  </Vertical>
                ),
                [AnySportPlayerScoreStats.FORM]: (
                  <AnyPlayerStatsIcon player={player} outlined />
                ),
                [BaseballPlayerScoreStats.L5]: player.L5,
                [BaseballPlayerScoreStats.L15]: player.L15,
                [AnySportPlayerScoreStats.PRICE]: (
                  <StartingPriceCell
                    rarity={Rarity.limited}
                    anyPlayer={player}
                    onListingsClick={onListingsClick}
                  />
                ),
              }
            : {
                [AnySportPlayerScoreStats.SCORE]: score,
                [BaseballPlayerScoreStats.AB]: hittingAtBase || 0,
                [BaseballPlayerScoreStats.R]: hittingRuns || 0,
                [BaseballPlayerScoreStats.H]: hits,
                [BaseballPlayerScoreStats.RBI]: hittingRunsBattedIn || 0,
                [BaseballPlayerScoreStats.HR]: hittingHomeRuns || 0,
                [BaseballPlayerScoreStats.BB]: hittingWalks || 0,
                [BaseballPlayerScoreStats.K]: hittingStrikeouts || 0,
                [BaseballPlayerScoreStats.AVG]: hits / (hittingAtBase || 1),
                [BaseballPlayerScoreStats.OBP]:
                  (hits + (hittingWalks || 0) + (hittingHitByPitch || 0)) /
                  ((hittingAtBase || 0) +
                    (hittingWalks || 0) +
                    (hittingHitByPitch || 0) || 1),
                [BaseballPlayerScoreStats.SLG]: hittingAtBase || 0,
                [BaseballPlayerScoreStats.MATCHUPS]: (
                  <Vertical>
                    <InlineGame
                      key={so5Score.anyGame.id}
                      game={so5Score.anyGame}
                      withTeamCode
                      withGameStatus
                    />
                  </Vertical>
                ),
              };

          return statsManager[PlayerScoreViewAttributes.PLAYER_SCORE].map(
            (stat, i) => {
              const CellComponent =
                i <
                statsManager[PlayerScoreViewAttributes.PLAYER_SCORE].length - 1
                  ? Cell
                  : LastCell;

              const scoreToDisplay =
                scoreStatus === ScoreStatus.PENDING ? (
                  <PlayerRotowireStatsProjection
                    player={player}
                    positions={player.anyPositions}
                    playerGameScores={eligibleMlbScoresToDisplay}
                    variant="hexagon"
                  />
                ) : (
                  <Score
                    hideReviewing
                    shape="HEXAGON"
                    score={so5Score.projectedScore}
                    scoreStatus={scoreStatus}
                    size={4}
                  />
                );

              return (
                <CellComponent key={stat}>
                  {typeof playerScoreStats[stat] === 'number' ? (
                    <>
                      {isUpcomingFixture ? (
                        <>
                          {stat === AnySportPlayerScoreStats.SCORE &&
                            !!scoreStatus &&
                            scoreToDisplay}
                          {[
                            BaseballPlayerScoreStats.L15,
                            BaseballPlayerScoreStats.L5,
                          ].includesValue(stat) && (
                            <Score
                              hideReviewing
                              shape="HEXAGON"
                              score={playerScoreStats[stat]}
                              scoreStatus={ScoreStatus.FINAL}
                              size={4}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {stat === AnySportPlayerScoreStats.SCORE &&
                            !!scoreStatus && (
                              <Score
                                hideReviewing
                                shape="HEXAGON"
                                score={playerScoreStats[stat]}
                                scoreStatus={scoreStatus}
                                size={4}
                              />
                            )}
                          {![
                            BaseballPlayerScoreStats.L15,
                            BaseballPlayerScoreStats.L5,
                            AnySportPlayerScoreStats.SCORE,
                          ].includesValue(stat) &&
                            !!scoreStatus && (
                              <Score
                                hideReviewing
                                shape={scoreShapeByMode[stat]}
                                score={playerScoreStats[stat] || 0}
                                scoreStatus={scoreStatus}
                                scoreType="PLAYER_SCORE"
                                animated={scoreShapeByMode[stat] === 'PILL'}
                                size={4}
                              />
                            )}
                        </>
                      )}
                    </>
                  ) : (
                    playerScoreStats[stat]
                  )}
                </CellComponent>
              );
            }
          );
        })}
      </ScoreCells>
    </>
  );
};

BaseballScoreCells.fragments = {
  fixturePlayer: gql`
    fragment BaseballScoreCells_fixturePlayer on FixturePlayer {
      id
      score
      eligibleSo5Scores {
        id
        score
        scoreStatus
        anyGame {
          id
          ...InlineGame_game
        }
        ... on BaseballPlayerGameScore {
          id
          ...PlayerRotowireStatsProjection_PlayerGameScoreInterface
          baseballPlayerGameStats {
            id
            hittingAtBase
            hittingRuns
            hittingSingles
            hittingDoubles
            hittingTriples
            hittingHomeRuns
            hittingRunsBattedIn
            hittingWalks
            hittingStrikeouts
            hittingHitByPitch
          }
        }
      }
      player {
        slug
        L15: averageScore(type: LAST_FIFTEEN_SO5_AVERAGE_SCORE)
        L5: averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
        ...AnyPlayerStatsIcon_anyPlayer
        ...StartingPriceCell_anyPlayer
        ...PlayerRotowireStatsProjection_AnyPlayerInterface
      }
    }
    ${InlineGame.fragments.game}
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${StartingPriceCell.fragments.anyPlayer}
    ${PlayerRotowireStatsProjection.fragments.anyPlayerInterface}
    ${PlayerRotowireStatsProjection.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<BaseballScoreCells_fixturePlayer>,
};
